import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { getSwalTheme, getSwalConfirmTheme } from '../utils/swalTheme';
import {
  AdminHeader,
  AdminSidebar,
  AdminStatsTab,
  AdminUsersTab,
  AdminListingsTab,
  AdminAuditLogsTab,
  AdminContactMessagesTab,
  AdminAdsTab,
  CreateAdModal,
  EditAdModal,
  AdminReportsTab,
  ReportConfigModal,
  Pagination,
  EditUserModal,
  CreateUserModal
} from '../components/admin';
import EditShiftModal from '../components/EditShiftModal';
import EditDriverPostModal from '../components/EditDriverPostModal';
import EditMedallionPostModal from '../components/EditMedallionPostModal';
import './AdminPage.css';

const AdminPage = () => {
  const { user: currentUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params
  const getInitialTab = () => searchParams.get('tab') || 'stats';
  const getInitialPage = () => parseInt(searchParams.get('page')) || 1;
  const getInitialFilters = () => ({
    role: searchParams.get('role') || '',
    status: searchParams.get('status') || '',
    search: searchParams.get('search') || '',
    action: searchParams.get('action') || '',
    entity_type: searchParams.get('entity_type') || '',
    category: searchParams.get('category') || ''
  });

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [listingType, setListingType] = useState(searchParams.get('listing') || 'car');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [driverPosts, setDriverPosts] = useState([]);
  const [medallionPosts, setMedallionPosts] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ page: getInitialPage(), pages: 1 });
  const [filters, setFilters] = useState(getInitialFilters);
  const [editModal, setEditModal] = useState(null);
  const [editShiftModal, setEditShiftModal] = useState(null);
  const [editDriverPostModal, setEditDriverPostModal] = useState(null);
  const [editMedallionPostModal, setEditMedallionPostModal] = useState(null);
  const [editAdModal, setEditAdModal] = useState(null);
  const [createAdModal, setCreateAdModal] = useState(false);
  const [createUserModal, setCreateUserModal] = useState(false);
  const [reportConfigs, setReportConfigs] = useState([]);
  const [reportConfigModal, setReportConfigModal] = useState(null);
  const [createReportConfigOpen, setCreateReportConfigOpen] = useState(false);

  // Update URL when state changes
  const updateURLParams = useCallback(() => {
    const params = new URLSearchParams();
    if (activeTab !== 'stats') params.set('tab', activeTab);
    if (activeTab === 'listings' && listingType !== 'car') params.set('listing', listingType);
    if (pagination.page > 1) params.set('page', pagination.page.toString());
    if (filters.role) params.set('role', filters.role);
    if (filters.status) params.set('status', filters.status);
    if (filters.search) params.set('search', filters.search);
    if (filters.action) params.set('action', filters.action);
    if (filters.entity_type) params.set('entity_type', filters.entity_type);
    if (filters.category) params.set('category', filters.category);

    setSearchParams(params, { replace: true });
  }, [activeTab, listingType, pagination.page, filters, setSearchParams]);

  useEffect(() => {
    updateURLParams();
  }, [updateURLParams]);

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    const fetchData = async () => {
      if (activeTab === 'stats') {
        await fetchStats(signal);
      } else if (activeTab === 'users') {
        await fetchUsers(signal);
      } else if (activeTab === 'listings') {
        if (listingType === 'car') {
          await fetchShifts(signal);
        } else if (listingType === 'driver') {
          await fetchDriverPosts(signal);
        } else if (listingType === 'medallion') {
          await fetchMedallionPosts(signal);
        }
      } else if (activeTab === 'ads') {
        await fetchAds(signal);
      } else if (activeTab === 'reports') {
        await fetchReportConfigs(signal);
      } else if (activeTab === 'contact-messages') {
        await fetchContactMessages(signal);
      } else if (activeTab === 'audit-logs') {
        await fetchAuditLogs(signal);
      }
    };

    fetchData();

    return () => abortController.abort();
  }, [activeTab, listingType, pagination.page, filters]);

  const fetchStats = async (signal) => {
    try {
      const response = await api.get('/admin/stats', { signal });
      setStats(response.data);
    } catch (err) {
      if (err.message !== 'Request was cancelled') {
        console.error('Failed to fetch stats:', err);
      }
    }
  };

  const fetchUsers = async (signal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      if (filters.role) params.append('role', filters.role);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/admin/users?${params.toString()}`, { signal });
      setUsers(response.data.users);
      setPagination(response.data.pagination);
    } catch (err) {
      if (err.message !== 'Request was cancelled') {
        console.error('Failed to fetch users:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchShifts = async (signal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/admin/shifts?${params.toString()}`, { signal });
      setShifts(response.data.shifts);
      setPagination(response.data.pagination);
    } catch (err) {
      if (err.message !== 'Request was cancelled') {
        console.error('Failed to fetch shifts:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchDriverPosts = async (signal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/admin/driver-posts?${params.toString()}`, { signal });
      setDriverPosts(response.data.posts);
      setPagination(response.data.pagination);
    } catch (err) {
      if (err.message !== 'Request was cancelled') {
        console.error('Failed to fetch driver posts:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchMedallionPosts = async (signal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/admin/medallion-posts?${params.toString()}`, { signal });
      setMedallionPosts(response.data.posts);
      setPagination(response.data.pagination);
    } catch (err) {
      if (err.message !== 'Request was cancelled') {
        console.error('Failed to fetch medallion posts:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchAds = async (signal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      if (filters.status) params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);

      const response = await api.get(`/admin/ads?${params.toString()}`, { signal });
      setAds(response.data.ads);
      setPagination(response.data.pagination);
    } catch (err) {
      if (err.message !== 'Request was cancelled') {
        console.error('Failed to fetch ads:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchReportConfigs = async (signal) => {
    setLoading(true);
    try {
      const response = await api.get('/admin/report-configs', { signal });
      setReportConfigs(response.data.configs || []);
    } catch (err) {
      if (err.message !== 'Request was cancelled') {
        console.error('Failed to fetch report configs:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateReportConfig = async (formData) => {
    try {
      await api.post('/admin/report-configs', formData);
      setCreateReportConfigOpen(false);
      fetchReportConfigs();
      Swal.fire({ ...getSwalTheme(), icon: 'success', title: 'Added', text: 'Report recipient added.' });
    } catch (err) {
      Swal.fire({ ...getSwalTheme(), icon: 'error', title: 'Error', text: err.message || 'Failed to add recipient' });
    }
  };

  const handleEditReportConfig = async (formData) => {
    try {
      await api.put(`/admin/report-configs/${reportConfigModal.id}`, formData);
      setReportConfigModal(null);
      fetchReportConfigs();
      Swal.fire({ ...getSwalTheme(), icon: 'success', title: 'Updated', text: 'Report recipient updated.' });
    } catch (err) {
      Swal.fire({ ...getSwalTheme(), icon: 'error', title: 'Error', text: err.message || 'Failed to update recipient' });
    }
  };

  const handleDeleteReportConfig = async (configId) => {
    const result = await Swal.fire({
      ...getSwalConfirmTheme(),
      title: 'Remove Recipient?',
      text: 'They will no longer receive weekly reports.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      confirmButtonText: 'Yes, remove'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/report-configs/${configId}`);
        fetchReportConfigs();
        Swal.fire({ ...getSwalTheme(), icon: 'success', title: 'Removed' });
      } catch (err) {
        Swal.fire({ ...getSwalTheme(), icon: 'error', title: 'Error', text: err.message });
      }
    }
  };

  const handleSendReportNow = async (config) => {
    const result = await Swal.fire({
      ...getSwalConfirmTheme(),
      title: 'Send Report Now?',
      text: `Send the weekly report to ${config.recipient_email} immediately?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Send'
    });

    if (!result.isConfirmed) return;

    try {
      const response = await api.post(`/admin/report-configs/${config.id}/send-now`, {});
      fetchReportConfigs();
      Swal.fire({
        ...getSwalTheme(),
        icon: 'success',
        title: 'Sent',
        text: `Report sent to ${config.recipient_email}.`
      });
    } catch (err) {
      Swal.fire({ ...getSwalTheme(), icon: 'error', title: 'Error', text: err.message || 'Failed to send report' });
    }
  };

  const fetchAuditLogs = async (signal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      if (filters.action) params.append('action', filters.action);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);

      const response = await api.get(`/admin/audit-logs?${params.toString()}`, { signal });
      setAuditLogs(response.data.logs);
      setPagination(response.data.pagination);
    } catch (err) {
      if (err.message !== 'Request was cancelled') {
        console.error('Failed to fetch audit logs:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchContactMessages = async (signal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', pagination.page);
      if (filters.status) params.append('status', filters.status);
      if (filters.category) params.append('category', filters.category);

      const response = await api.get(`/admin/contact-messages?${params.toString()}`, { signal });
      setContactMessages(response.data.messages);
      setPagination(response.data.pagination);
    } catch (err) {
      if (err.message !== 'Request was cancelled') {
        console.error('Failed to fetch contact messages:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleContactStatusChange = async (messageId, status) => {
    try {
      await api.patch(`/admin/contact-messages/${messageId}/status`, { status });
      fetchContactMessages();
    } catch (err) {
      Swal.fire({
        ...getSwalTheme(),
        icon: 'error',
        title: 'Error',
        text: err.message || 'Failed to update status'
      });
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    const result = await Swal.fire({
      ...getSwalConfirmTheme(),
      title: 'Delete User?',
      text: `Are you sure you want to delete ${userName}? This will also delete all their posts.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      confirmButtonText: 'Yes, delete'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/users/${userId}`);
        fetchUsers();
        Swal.fire({
          ...getSwalTheme(),
          icon: 'success',
          title: 'Deleted',
          text: 'User has been deleted.'
        });
      } catch (err) {
        Swal.fire({
          ...getSwalTheme(),
          icon: 'error',
          title: 'Error',
          text: err.message || 'Failed to delete user'
        });
      }
    }
  };

  const handleDeleteShift = async (shiftId) => {
    const result = await Swal.fire({
      ...getSwalConfirmTheme(),
      title: 'Delete Car Listing?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      confirmButtonText: 'Yes, delete'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/shifts/${shiftId}`);
        fetchShifts();
        Swal.fire({
          ...getSwalTheme(),
          icon: 'success',
          title: 'Deleted'
        });
      } catch (err) {
        Swal.fire({
          ...getSwalTheme(),
          icon: 'error',
          title: 'Error',
          text: err.message
        });
      }
    }
  };

  const handleDeleteDriverPost = async (postId) => {
    const result = await Swal.fire({
      ...getSwalConfirmTheme(),
      title: 'Delete Driver Listing?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      confirmButtonText: 'Yes, delete'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/driver-posts/${postId}`);
        fetchDriverPosts();
        Swal.fire({
          ...getSwalTheme(),
          icon: 'success',
          title: 'Deleted'
        });
      } catch (err) {
        Swal.fire({
          ...getSwalTheme(),
          icon: 'error',
          title: 'Error',
          text: err.message
        });
      }
    }
  };

  const handleEditUser = async (formData) => {
    try {
      await api.put(`/admin/users/${editModal.id}`, formData);
      setEditModal(null);
      fetchUsers();
      Swal.fire({
        ...getSwalTheme(),
        icon: 'success',
        title: 'Updated',
        text: 'User has been updated.'
      });
    } catch (err) {
      Swal.fire({
        ...getSwalTheme(),
        icon: 'error',
        title: 'Error',
        text: err.message
      });
    }
  };

  const handleCreateUser = async (formData) => {
    try {
      await api.post('/admin/users', formData);
      setCreateUserModal(false);
      fetchUsers();
      Swal.fire({
        ...getSwalTheme(),
        icon: 'success',
        title: 'Created',
        text: 'User has been created.'
      });
    } catch (err) {
      Swal.fire({
        ...getSwalTheme(),
        icon: 'error',
        title: 'Error',
        text: err.message || 'Failed to create user'
      });
    }
  };

  const handleBulkDeleteUsers = async (ids) => {
    const result = await Swal.fire({
      ...getSwalConfirmTheme(),
      title: 'Delete Users?',
      text: `Are you sure you want to delete ${ids.length} user(s)? This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      confirmButtonText: 'Yes, delete all'
    });

    if (result.isConfirmed) {
      try {
        await api.delete('/admin/users/bulk', { ids });
        fetchUsers();
        Swal.fire({
          ...getSwalTheme(),
          icon: 'success',
          title: 'Deleted',
          text: `${ids.length} user(s) have been deleted.`
        });
      } catch (err) {
        Swal.fire({
          ...getSwalTheme(),
          icon: 'error',
          title: 'Error',
          text: err.message || 'Failed to delete users'
        });
      }
    }
  };

  const handleBulkStatusChangeUsers = async (ids, status) => {
    const result = await Swal.fire({
      ...getSwalConfirmTheme(),
      title: 'Update Status?',
      text: `Change status of ${ids.length} user(s) to "${status}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, update'
    });

    if (result.isConfirmed) {
      try {
        await api.patch('/admin/users/bulk/status', { ids, status });
        fetchUsers();
        Swal.fire({
          ...getSwalTheme(),
          icon: 'success',
          title: 'Updated',
          text: `${ids.length} user(s) have been updated.`
        });
      } catch (err) {
        Swal.fire({
          ...getSwalTheme(),
          icon: 'error',
          title: 'Error',
          text: err.message || 'Failed to update users'
        });
      }
    }
  };

  const handleBulkDeleteShifts = async (ids) => {
    const result = await Swal.fire({
      ...getSwalConfirmTheme(),
      title: 'Delete Car Listings?',
      text: `Are you sure you want to delete ${ids.length} car listing(s)? This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      confirmButtonText: 'Yes, delete all'
    });

    if (result.isConfirmed) {
      try {
        await api.delete('/admin/shifts/bulk', { ids });
        fetchShifts();
        Swal.fire({
          ...getSwalTheme(),
          icon: 'success',
          title: 'Deleted',
          text: `${ids.length} car listing(s) have been deleted.`
        });
      } catch (err) {
        Swal.fire({
          ...getSwalTheme(),
          icon: 'error',
          title: 'Error',
          text: err.message || 'Failed to delete car listings'
        });
      }
    }
  };

  const handleBulkStatusChangeShifts = async (ids, status) => {
    const result = await Swal.fire({
      ...getSwalConfirmTheme(),
      title: 'Update Status?',
      text: `Change status of ${ids.length} car listing(s) to "${status}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, update'
    });

    if (result.isConfirmed) {
      try {
        await api.patch('/admin/shifts/bulk/status', { ids, status });
        fetchShifts();
        Swal.fire({
          ...getSwalTheme(),
          icon: 'success',
          title: 'Updated',
          text: `${ids.length} car listing(s) have been updated.`
        });
      } catch (err) {
        Swal.fire({
          ...getSwalTheme(),
          icon: 'error',
          title: 'Error',
          text: err.message || 'Failed to update car listings'
        });
      }
    }
  };

  const handleDeleteMedallionPost = async (postId) => {
    const result = await Swal.fire({
      ...getSwalConfirmTheme(),
      title: 'Delete Medallion Listing?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      confirmButtonText: 'Yes, delete'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/medallion-posts/${postId}`);
        fetchMedallionPosts();
        Swal.fire({ ...getSwalTheme(), icon: 'success', title: 'Deleted' });
      } catch (err) {
        Swal.fire({ ...getSwalTheme(), icon: 'error', title: 'Error', text: err.message });
      }
    }
  };

  const handleBulkDeleteMedallionPosts = async (ids) => {
    const result = await Swal.fire({
      ...getSwalConfirmTheme(),
      title: 'Delete Medallion Listings?',
      text: `Are you sure you want to delete ${ids.length} medallion listing(s)? This cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      confirmButtonText: 'Yes, delete all'
    });

    if (result.isConfirmed) {
      try {
        await api.delete('/admin/medallion-posts/bulk', { ids });
        fetchMedallionPosts();
        Swal.fire({ ...getSwalTheme(), icon: 'success', title: 'Deleted', text: `${ids.length} medallion listing(s) deleted.` });
      } catch (err) {
        Swal.fire({ ...getSwalTheme(), icon: 'error', title: 'Error', text: err.message || 'Failed to delete' });
      }
    }
  };

  const handleCreateAd = async (formData) => {
    try {
      await api.post('/admin/ads', formData);
      setCreateAdModal(false);
      fetchAds();
      Swal.fire({ ...getSwalTheme(), icon: 'success', title: 'Created', text: 'Ad has been created.' });
    } catch (err) {
      Swal.fire({ ...getSwalTheme(), icon: 'error', title: 'Error', text: err.message || 'Failed to create ad' });
    }
  };

  const handleEditAd = async (formData) => {
    try {
      await api.put(`/admin/ads/${editAdModal.id}`, formData);
      setEditAdModal(null);
      fetchAds();
      Swal.fire({ ...getSwalTheme(), icon: 'success', title: 'Updated', text: 'Ad has been updated.' });
    } catch (err) {
      Swal.fire({ ...getSwalTheme(), icon: 'error', title: 'Error', text: err.message || 'Failed to update ad' });
    }
  };

  const handleDeleteAd = async (adId) => {
    const result = await Swal.fire({
      ...getSwalConfirmTheme(),
      title: 'Delete Ad?',
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#EF4444',
      confirmButtonText: 'Yes, delete'
    });

    if (result.isConfirmed) {
      try {
        await api.delete(`/admin/ads/${adId}`);
        fetchAds();
        Swal.fire({ ...getSwalTheme(), icon: 'success', title: 'Deleted' });
      } catch (err) {
        Swal.fire({ ...getSwalTheme(), icon: 'error', title: 'Error', text: err.message });
      }
    }
  };

  const handleBulkStatusChangeMedallionPosts = async (ids, status) => {
    const result = await Swal.fire({
      ...getSwalConfirmTheme(),
      title: 'Update Status?',
      text: `Change status of ${ids.length} medallion listing(s) to "${status}"?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, update'
    });

    if (result.isConfirmed) {
      try {
        await api.patch('/admin/medallion-posts/bulk/status', { ids, status });
        fetchMedallionPosts();
        Swal.fire({ ...getSwalTheme(), icon: 'success', title: 'Updated', text: `${ids.length} medallion listing(s) updated.` });
      } catch (err) {
        Swal.fire({ ...getSwalTheme(), icon: 'error', title: 'Error', text: err.message || 'Failed to update' });
      }
    }
  };

  const resetPagination = () => {
    setPagination({ page: 1, pages: 1 });
  };

  return (
    <div className="admin-page">
      <AdminSidebar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        resetPagination={resetPagination}
      />

      <div className="admin-content">
        <AdminHeader />
        {activeTab === 'stats' && (
          <AdminStatsTab
            stats={stats}
            loading={loading}
            onRefresh={() => fetchStats()}
          />
        )}

        {activeTab === 'users' && (
          <AdminUsersTab
            users={users}
            loading={loading}
            filters={filters}
            setFilters={setFilters}
            onSearch={fetchUsers}
            onEdit={(user) => setEditModal({ type: 'user', ...user })}
            onDelete={handleDeleteUser}
            onCreateUser={() => setCreateUserModal(true)}
            onBulkDelete={handleBulkDeleteUsers}
            onBulkStatusChange={handleBulkStatusChangeUsers}
          />
        )}

        {activeTab === 'listings' && (
          <AdminListingsTab
            listingType={listingType}
            setListingType={(type) => {
              setListingType(type);
              resetPagination();
            }}
            shifts={shifts}
            driverPosts={driverPosts}
            medallionPosts={medallionPosts}
            loading={loading}
            filters={filters}
            setFilters={setFilters}
            onSearchShifts={fetchShifts}
            onSearchDriverPosts={fetchDriverPosts}
            onSearchMedallionPosts={fetchMedallionPosts}
            onEditShift={setEditShiftModal}
            onDeleteShift={handleDeleteShift}
            onEditDriverPost={setEditDriverPostModal}
            onDeleteDriverPost={handleDeleteDriverPost}
            onEditMedallionPost={setEditMedallionPostModal}
            onDeleteMedallionPost={handleDeleteMedallionPost}
            onBulkDeleteShifts={handleBulkDeleteShifts}
            onBulkStatusChangeShifts={handleBulkStatusChangeShifts}
            onBulkDeleteMedallionPosts={handleBulkDeleteMedallionPosts}
            onBulkStatusChangeMedallionPosts={handleBulkStatusChangeMedallionPosts}
          />
        )}

        {activeTab === 'contact-messages' && (
          <AdminContactMessagesTab
            messages={contactMessages}
            loading={loading}
            filters={filters}
            setFilters={setFilters}
            onSearch={fetchContactMessages}
            onStatusChange={handleContactStatusChange}
          />
        )}

        {activeTab === 'ads' && (
          <AdminAdsTab
            ads={ads}
            loading={loading}
            filters={filters}
            setFilters={setFilters}
            onSearch={fetchAds}
            onEdit={(ad) => setEditAdModal(ad)}
            onDelete={handleDeleteAd}
            onCreate={() => setCreateAdModal(true)}
          />
        )}

        {activeTab === 'audit-logs' && (
          <AdminAuditLogsTab
            logs={auditLogs}
            loading={loading}
            filters={filters}
            setFilters={setFilters}
            onSearch={fetchAuditLogs}
          />
        )}

        {activeTab === 'reports' && (
          <AdminReportsTab
            reportConfigs={reportConfigs}
            loading={loading}
            onCreate={() => setCreateReportConfigOpen(true)}
            onEdit={(cfg) => setReportConfigModal(cfg)}
            onDelete={handleDeleteReportConfig}
            onSendNow={handleSendReportNow}
          />
        )}

        {activeTab !== 'stats' && activeTab !== 'reports' && (
          <Pagination pagination={pagination} setPagination={setPagination} />
        )}
      </div>

      {editModal?.type === 'user' && (
        <EditUserModal
          user={editModal}
          currentUser={currentUser}
          onClose={() => setEditModal(null)}
          onSave={handleEditUser}
        />
      )}

      {editShiftModal && (
        <EditShiftModal
          shift={editShiftModal}
          onClose={() => setEditShiftModal(null)}
          onUpdate={fetchShifts}
          isAdmin={true}
        />
      )}

      {editDriverPostModal && (
        <EditDriverPostModal
          post={editDriverPostModal}
          onClose={() => setEditDriverPostModal(null)}
          onUpdate={fetchDriverPosts}
          isAdmin={true}
        />
      )}

      {createAdModal && (
        <CreateAdModal
          onClose={() => setCreateAdModal(false)}
          onSave={handleCreateAd}
        />
      )}

      {editAdModal && (
        <EditAdModal
          ad={editAdModal}
          onClose={() => setEditAdModal(null)}
          onSave={handleEditAd}
        />
      )}

      {editMedallionPostModal && (
        <EditMedallionPostModal
          post={editMedallionPostModal}
          onClose={() => setEditMedallionPostModal(null)}
          onUpdate={fetchMedallionPosts}
          isAdmin={true}
        />
      )}

      {createUserModal && (
        <CreateUserModal
          onClose={() => setCreateUserModal(false)}
          onSave={handleCreateUser}
        />
      )}

      {createReportConfigOpen && (
        <ReportConfigModal
          onClose={() => setCreateReportConfigOpen(false)}
          onSave={handleCreateReportConfig}
        />
      )}

      {reportConfigModal && (
        <ReportConfigModal
          initialConfig={reportConfigModal}
          onClose={() => setReportConfigModal(null)}
          onSave={handleEditReportConfig}
        />
      )}
    </div>
  );
};

export default AdminPage;
